import { useEffect, useState } from 'react';
import { Button, Input, RadixSelect, StatusBadge, Switch } from '@/components/ui';
import { formatDateTime, formatRate } from '../lib/format';
import {
  EXCHANGE_RATE_MODES,
  type ExchangeRate,
  type ExchangeRateCurrency,
  type ExchangeRateMode,
  type ExchangeRateUpdatePayload,
} from '../types';

const CURRENCY_LABELS: Record<ExchangeRateCurrency, string> = {
  USD: '$ USD',
  JPY: '¥ JPY',
  CNY: '¥ CNY',
};

const MODE_LABELS: Record<ExchangeRateMode, string> = {
  AUTO: '자동(관세청)',
  MANUAL: '수동',
};

const MODE_OPTIONS = EXCHANGE_RATE_MODES.map((m) => ({ value: m, label: MODE_LABELS[m] }));

interface RowDraft {
  mode: ExchangeRateMode;
  /** MANUAL 모드 최종 환율 입력값(문자열). */
  manualRate: string;
  /** AUTO 모드 조정값 입력값(문자열, ±). */
  adjustment: string;
  active: boolean;
}

const toDraft = (rate: ExchangeRate | undefined): RowDraft => ({
  mode: rate?.mode ?? 'MANUAL',
  manualRate: rate ? String(rate.krwPerUnit) : '',
  adjustment: rate?.adjustment !== undefined && rate.adjustment !== null ? String(rate.adjustment) : '0',
  active: rate?.active ?? false,
});

export interface ExchangeRateRowProps {
  currency: ExchangeRateCurrency;
  rate: ExchangeRate | undefined;
  canWrite: boolean;
  saving: boolean;
  onSave: (currency: ExchangeRateCurrency, payload: ExchangeRateUpdatePayload) => void;
}

/**
 * 통화별 환율 편집 행. 모드(자동/수동) 토글에 따라 입력 UI 가 갈린다.
 * - MANUAL: 최종 환율을 직접 입력.
 * - AUTO: 관세청 기준환율(baseRate, 읽기전용) + 조정값(±) 입력 → 최종 = base + adjustment.
 */
export const ExchangeRateRow = ({ currency, rate, canWrite, saving, onSave }: ExchangeRateRowProps) => {
  const [draft, setDraft] = useState<RowDraft>(() => toDraft(rate));

  // 서버 값 갱신(저장 성공 후 invalidate) 시 draft 동기화.
  useEffect(() => {
    setDraft(toDraft(rate));
  }, [rate]);

  const isAuto = draft.mode === 'AUTO';
  const baseRate = rate?.baseRate ?? null;

  const parsedManual = Number(draft.manualRate);
  const parsedAdjustment = Number(draft.adjustment);
  const adjustmentValid = draft.adjustment.trim() !== '' && Number.isFinite(parsedAdjustment);
  const manualValid = draft.manualRate.trim() !== '' && Number.isFinite(parsedManual) && parsedManual > 0;

  // AUTO 최종환율 미리보기 = base + adjustment (base 없으면 계산 불가).
  const autoFinal =
    isAuto && baseRate !== null && adjustmentValid ? baseRate + parsedAdjustment : null;

  const valid = isAuto ? (baseRate === null || adjustmentValid) : manualValid;

  const dirty =
    draft.mode !== (rate?.mode ?? 'MANUAL') ||
    draft.active !== (rate?.active ?? false) ||
    (isAuto
      ? draft.adjustment !== (rate?.adjustment != null ? String(rate.adjustment) : '0')
      : draft.manualRate !== (rate ? String(rate.krwPerUnit) : ''));

  const handleSave = () => {
    if (isAuto) {
      // AUTO: 조정값만 의미. 최종환율은 BE 가 base+adjustment 로 산출한다. 미리보기값(base 미조회 시
      // null)·기존값이 모두 없으면 krwPerUnit 을 생략한다 — 0 을 보내면 BE @DecimalMin(positive) 거부.
      const autoKrwPerUnit = autoFinal ?? rate?.krwPerUnit ?? null;
      onSave(currency, {
        mode: 'AUTO',
        adjustment: adjustmentValid ? parsedAdjustment : 0,
        ...(autoKrwPerUnit != null ? { krwPerUnit: autoKrwPerUnit } : {}),
        active: draft.active,
      });
    } else {
      onSave(currency, {
        mode: 'MANUAL',
        krwPerUnit: parsedManual,
        active: draft.active,
      });
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-md px-4 py-3"
      style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
      data-testid={`exchange-rate-row-${currency}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div
          className="flex h-10 min-w-[88px] items-center justify-center rounded-md text-xs font-semibold tabular-nums"
          style={{
            background: 'var(--surface-raised)',
            border: '1px solid var(--divider-strong)',
            color: 'var(--content-muted)',
          }}
        >
          {CURRENCY_LABELS[currency]}
        </div>

        {/* 모드 토글 */}
        <div className="w-[150px]">
          <RadixSelect
            value={draft.mode}
            onChange={(v) => setDraft((prev) => ({ ...prev, mode: v as ExchangeRateMode }))}
            options={MODE_OPTIONS}
            disabled={!canWrite || saving}
            aria-label={`${currency} 갱신 모드`}
            data-testid={`exchange-rate-mode-${currency}`}
          />
        </div>

        {/* 활성 토글 */}
        <label className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--content-default)' }}>
          <Switch
            checked={draft.active}
            disabled={!canWrite || saving}
            onCheckedChange={(v) => setDraft((prev) => ({ ...prev, active: v }))}
            aria-label={`${currency} 환율 활성`}
          />
          {draft.active ? (
            <StatusBadge label="활성" variant="success" />
          ) : (
            <StatusBadge label="비활성" variant="neutral" />
          )}
        </label>

        {canWrite ? (
          <Button
            size="sm"
            className="ml-auto"
            disabled={!dirty || !valid || saving}
            onClick={handleSave}
            data-testid={`exchange-rate-save-${currency}`}
          >
            저장
          </Button>
        ) : null}
      </div>

      {/* 모드별 입력 영역 */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pl-1">
        {isAuto ? (
          <>
            <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
              기준 {formatRate(baseRate)} KRW
            </span>
            <label className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--content-muted)' }}>
              조정(±)
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                disabled={!canWrite || saving}
                value={draft.adjustment}
                aria-label={`${currency} 조정값`}
                className="max-w-[140px] tabular-nums"
                data-testid={`exchange-rate-adjustment-${currency}`}
                onChange={(e) => setDraft((prev) => ({ ...prev, adjustment: e.target.value }))}
              />
            </label>
            <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-strong)', fontWeight: 600 }}>
              최종 1 {currency} = {formatRate(autoFinal ?? rate?.krwPerUnit ?? null)} KRW
            </span>
            {baseRate === null ? (
              <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                (기준 환율은 자동 갱신 후 표시됩니다)
              </span>
            ) : null}
          </>
        ) : (
          <>
            <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
              1 {currency} =
            </span>
            <Input
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              disabled={!canWrite || saving}
              value={draft.manualRate}
              aria-label={`${currency} 환율 (KRW)`}
              className="max-w-[180px] tabular-nums"
              data-testid={`exchange-rate-manual-${currency}`}
              onChange={(e) => setDraft((prev) => ({ ...prev, manualRate: e.target.value }))}
            />
            <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
              KRW
            </span>
          </>
        )}
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          최근 수정 {formatDateTime(rate?.updatedAt)}
        </span>
      </div>
    </div>
  );
};
